import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useLanguage } from "../context/LanguageContext";
import OptimizedImage from "../components/OptimizedImage";
import { Truck, Package, Scissors, Shield, Check, ChevronRight, Car, Factory, Heart, Award } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Map icon names (string) to lucide components
const ICONS = { Truck, Package, Scissors, Shield, Car, Factory, Heart, Award };

const ServicesPage = () => {
  const { t, lang } = useLanguage();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/services`)
      .then((r) => setServices(r.data || []))
      .catch(() => setServices([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="pt-20" data-testid="services-page">
      {/* Header */}
      <section className="py-24 bg-zinc-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 mb-4">{t.nav.services}</p>
          <h1 className="text-5xl sm:text-6xl font-black tracking-tighter text-[#0A0A0A] mb-6" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            {t.services.title}
          </h1>
          <p className="text-xl text-zinc-600 max-w-2xl">{t.services.subtitle}</p>
        </div>
      </section>

      {loading ? (
        <section className="py-24 text-center text-zinc-500">{t.common.loading}</section>
      ) : (
        services.map((service, index) => {
          const Icon = ICONS[service.icon] || Package;
          const title = lang === "sq" ? service.title_sq : service.title_en;
          const desc = lang === "sq" ? service.desc_sq : service.desc_en;
          const features = lang === "sq" ? (service.features_sq || []) : (service.features_en || []);
          return (
            <section key={service.id} className={`py-24 ${index % 2 === 0 ? "bg-white" : "bg-zinc-50"}`}>
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className={`grid lg:grid-cols-2 gap-16 items-center ${index % 2 === 1 ? "lg:flex-row-reverse" : ""}`}>
                  <div className={index % 2 === 1 ? "lg:order-2" : ""}>
                    <Icon className="w-12 h-12 text-[#0A0A0A] mb-6" />
                    <h2 className="text-4xl font-bold tracking-tight text-[#0A0A0A] mb-6" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                      {title}
                    </h2>
                    <p className="text-zinc-600 text-lg mb-8">{desc}</p>
                    {features.length > 0 && (
                      <ul className="space-y-3 mb-8">
                        {features.map((feature, i) => (
                          <li key={i} className="flex items-center gap-3">
                            <Check className="w-5 h-5 text-[#0A0A0A]" />
                            <span className="text-zinc-700">{feature}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                    <Link
                      to="/contact"
                      className="inline-flex items-center gap-2 bg-[#0A0A0A] text-white px-6 py-3 font-medium hover:bg-zinc-800 transition-colors"
                    >
                      {t.hero.cta} <ChevronRight className="w-4 h-4" />
                    </Link>
                  </div>
                  {service.image && (
                    <div className={`relative h-[400px] lg:h-[500px] ${index % 2 === 1 ? "lg:order-1" : ""}`}>
                      <OptimizedImage
                        src={service.image}
                        alt={title}
                        sizes="(max-width: 1024px) 100vw, 50vw"
                        className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                      />
                    </div>
                  )}
                </div>
              </div>
            </section>
          );
        })
      )}

      {/* CTA */}
      <section className="py-24 bg-[#0A0A0A] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold tracking-tight mb-6" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            {t.contact.title}
          </h2>
          <p className="text-zinc-400 text-lg mb-8 max-w-xl mx-auto">{t.contact.subtitle}</p>
          <Link to="/contact" className="inline-flex items-center gap-2 bg-white text-[#0A0A0A] px-8 py-4 font-medium hover:bg-zinc-100 transition-colors">
            {t.hero.cta} <ChevronRight className="w-5 h-5" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
